// Reusable card showing one variation + (for office) inline price/status editor.
import { useState } from "react";
import { FileText, DollarSign, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_STYLES = {
  requested: { bg: "bg-amber-100 text-amber-800 border-amber-400", label: "AWAITING PRICE" },
  approved: { bg: "bg-emerald-100 text-emerald-800 border-emerald-400", label: "APPROVED" },
  rejected: { bg: "bg-zinc-200 text-zinc-700 border-zinc-400", label: "REJECTED" },
  invoiced: { bg: "bg-sky-100 text-sky-800 border-sky-400", label: "INVOICED" },
};

export function VariationCard({ variation, canEdit, onChanged, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(variation.price ?? "");
  const [notes, setNotes] = useState(variation.office_notes ?? "");
  const [busy, setBusy] = useState(false);
  const style = STATUS_STYLES[variation.status] || STATUS_STYLES.requested;

  const setStatus = async (status, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/variations/${variation.id}`, {
        status,
        ...extra,
        ...(price !== "" && !isNaN(Number(price)) ? { price: Number(price) } : {}),
        ...(notes && notes !== variation.office_notes ? { office_notes: notes } : {}),
      });
      toast.success(`Variation ${status}`);
      setEditing(false);
      onChanged?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const saveOnly = async () => {
    if (price === "" && !notes) return;
    setBusy(true);
    try {
      const payload = {};
      if (price !== "") payload.price = Number(price);
      if (notes !== variation.office_notes) payload.office_notes = notes;
      const { data } = await api.patch(`/variations/${variation.id}`, payload);
      toast.success("Saved");
      setEditing(false);
      onChanged?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this variation? This can't be undone.")) return;
    try {
      await api.delete(`/variations/${variation.id}`);
      onDeleted?.(variation.id);
      toast.success("Variation deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const created = (variation.created_at || "").slice(0, 10);

  return (
    <div className="border-2 border-zinc-300 bg-white" data-testid={`variation-card-${variation.id}`}>
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono font-bold uppercase tracking-widest text-xs bg-zinc-900 text-white px-1.5 py-0.5">
            {variation.variation_number || "V?"}
          </span>
          {variation.job_number && (
            <span className="font-mono text-xs text-zinc-600">{variation.job_number}</span>
          )}
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${style.bg}`}>
            {style.label}
          </span>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/variations/${variation.id}/pdf`}
          target="_blank"
          rel="noreferrer"
          className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 hover:text-[#FF5F15] inline-flex items-center gap-1"
          data-testid={`variation-pdf-${variation.id}`}
        >
          <FileText className="w-3 h-3" /> Signed PDF
        </a>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <div className="label-tag text-zinc-500 mb-1">Scope</div>
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{variation.scope}</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="label-tag text-zinc-500">Customer</div>
            <div className="font-bold mt-0.5">{variation.client_name}</div>
          </div>
          <div>
            <div className="label-tag text-zinc-500">Signed</div>
            <div className="font-mono mt-0.5">{created}</div>
          </div>
          <div>
            <div className="label-tag text-zinc-500">Requested by</div>
            <div className="font-mono mt-0.5">{variation.requested_by_name}</div>
          </div>
          <div>
            <div className="label-tag text-zinc-500">Agreed price</div>
            <div className="font-bold mt-0.5">
              {variation.price != null ? `$${Number(variation.price).toFixed(2)}` : <span className="text-zinc-400">—</span>}
            </div>
          </div>
        </div>
        {variation.client_signature && (
          <div>
            <div className="label-tag text-zinc-500 mb-1">Signature</div>
            <img
              src={variation.client_signature}
              alt={`${variation.client_name} signature`}
              className="h-20 border border-zinc-300 bg-white"
              data-testid={`variation-signature-${variation.id}`}
            />
          </div>
        )}
        {variation.office_notes && !editing && (
          <div>
            <div className="label-tag text-zinc-500 mb-1">Office notes</div>
            <p className="text-sm whitespace-pre-wrap text-zinc-700">{variation.office_notes}</p>
          </div>
        )}
        {canEdit && editing && (
          <div className="space-y-2 border-t border-zinc-200 pt-3 mt-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="label-tag" htmlFor={`var-price-${variation.id}`}>Price ($NZD)</label>
                <Input
                  id={`var-price-${variation.id}`}
                  type="number"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="mt-1 rounded-sm border-zinc-400"
                  placeholder="0.00"
                  data-testid={`variation-price-input-${variation.id}`}
                />
              </div>
            </div>
            <div>
              <label className="label-tag" htmlFor={`var-notes-${variation.id}`}>Office notes (optional)</label>
              <Textarea
                id={`var-notes-${variation.id}`}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="mt-1 rounded-sm border-zinc-400 text-sm"
                placeholder="Why this price / labour breakdown / any disputes"
                data-testid={`variation-notes-input-${variation.id}`}
              />
            </div>
          </div>
        )}
      </div>
      {canEdit && (
        <div className="px-4 py-2 border-t border-zinc-200 bg-zinc-50 flex items-center gap-2 flex-wrap">
          {editing ? (
            <>
              <Button
                size="sm"
                onClick={saveOnly}
                disabled={busy}
                className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white uppercase tracking-wider font-bold text-xs"
                data-testid={`variation-save-${variation.id}`}
              >
                <DollarSign className="w-3 h-3 mr-1" /> Save price & notes
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setStatus("approved")}
                disabled={busy}
                className="rounded-sm uppercase tracking-wider font-bold text-xs border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                data-testid={`variation-approve-${variation.id}`}
              >
                <Check className="w-3 h-3 mr-1" /> Approve
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
                className="rounded-sm uppercase tracking-wider font-bold text-xs ml-auto"
                data-testid={`variation-cancel-edit-${variation.id}`}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              {variation.status === "requested" && (
                <Button
                  size="sm"
                  onClick={() => setEditing(true)}
                  className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
                  data-testid={`variation-price-${variation.id}`}
                >
                  Set price
                </Button>
              )}
              {variation.status === "approved" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setStatus("invoiced")}
                  disabled={busy}
                  className="rounded-sm uppercase tracking-wider font-bold text-xs border-sky-500 text-sky-700 hover:bg-sky-50"
                  data-testid={`variation-invoiced-${variation.id}`}
                >
                  Mark invoiced
                </Button>
              )}
              {(variation.status === "requested" || variation.status === "approved") && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setStatus("rejected")}
                  disabled={busy}
                  className="rounded-sm uppercase tracking-wider font-bold text-xs text-zinc-600"
                  data-testid={`variation-reject-${variation.id}`}
                >
                  <X className="w-3 h-3 mr-1" /> Reject
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={del}
                className="rounded-sm uppercase tracking-wider font-bold text-[10px] text-zinc-500 ml-auto"
                data-testid={`variation-delete-${variation.id}`}
              >
                Delete
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
